import { inject, Injectable } from '@angular/core';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { GenericDynamicComponent } from 'src/app/core/classes/DynamicComponent';
import { HeroBannerContentfulModelEnumerate } from 'src/app/core/enumerates/hero-banner-page-component-type.enumerate';
import { PageLayoutType } from 'src/app/core/enumerates/page-layout-type.enumerate';
import {
  HeroBannerContentfulPageComponent,
  HeroBannerPageComponentBase,
  PageComponentService,
} from 'src/app/core/interfaces/Builder';

@Injectable({
  providedIn: 'root',
})
export class HeroBannerPageComponentService implements PageComponentService {
  #pageComponents: { [id: string]: { [subtype: string]: () => Promise<GenericDynamicComponent> } } = {};
  #logger = inject(GenericLogger);

  private constructor() {
    this.#registerPageComponents();
  }

  async getPageComponent(pageComponent: HeroBannerPageComponentBase): Promise<GenericDynamicComponent | undefined> {
    const type = pageComponent.id;
    let subtype = 'default';

    if (this.#isContentfulPageComponent(pageComponent)) {
      subtype = pageComponent.options.contentfulModel || 'default';
    }

    if (!this.#pageComponents[type]) {
      this.#logPageComponentError(pageComponent);
      return;
    }

    const loadComponentFn = this.#pageComponents[type][subtype];
    if (loadComponentFn) {
      const module = await loadComponentFn();
      this.#logger.debug({ msg: `Loaded dynamic component of type: ${type} - ${subtype}` });
      return module;
    } else {
      this.#logPageComponentError(pageComponent);
      return undefined;
    }
  }

  #isContentfulPageComponent(pageComponent: HeroBannerPageComponentBase): pageComponent is HeroBannerContentfulPageComponent {
    return pageComponent.id === PageLayoutType.HERO_BANNER_CONTENTFUL;
  }

  #logPageComponentError(pageComponent: HeroBannerPageComponentBase) {
    const msg = `Attempt to load an unknown page component type, this is usually a unknown/misconfigured page component in the page-creator builder model. ID: ${pageComponent.id}`;
    this.#logger.error({ msg, data: pageComponent });
  }

  #addPageComponent(
    type: PageLayoutType.HERO_BANNER_BUILDER | PageLayoutType.HERO_BANNER_CONTENTFUL,
    subtype: HeroBannerContentfulModelEnumerate | 'default',
    loadComponentFn: () => any,
  ): void {
    if (!this.#pageComponents[type]) {
      this.#pageComponents[type] = {};
    }

    if (this.#pageComponents[type][subtype]) {
      console.warn(`Registering Hero Banner page component with repeated type: ${type} and subtipe: ${subtype}`);
      return;
    }

    this.#pageComponents[type][subtype] = loadComponentFn;
  }

  #registerPageComponents() {
    this.#addPageComponent(PageLayoutType.HERO_BANNER_CONTENTFUL, HeroBannerContentfulModelEnumerate.BLOCK_CONTENT_BLOCK, () =>
      import(
        '../../../shared/components/contentful/contentful-block-content-block/contentful-block-content-block.component'
      ).then((m) => m.AppContentfulBlockContentBlockComponent),
    );
    this.#addPageComponent(
      PageLayoutType.HERO_BANNER_CONTENTFUL,
      HeroBannerContentfulModelEnumerate.BLOCK_APP_DOWNLOAD_BLOCK,
      () =>
        import(
          '../../../shared/components/contentful/contentful-block-app-download-block/contentful-block-app-download-block.component'
        ).then((m) => m.AppContentfulBlockAppDownloadBlockComponent),
    );
    this.#addPageComponent(PageLayoutType.HERO_BANNER_CONTENTFUL, HeroBannerContentfulModelEnumerate.BLOCK_CONTENT_VIDEO, () =>
      import(
        '../../../shared/components/contentful/contentful-block-content-video/contentful-block-content-video.component'
      ).then((m) => m.AppContentfulBlockContentVideoComponent),
    );
    this.#addPageComponent(PageLayoutType.HERO_BANNER_BUILDER, 'default', () =>
      import('../../../shared/components/builder/hero-banner/hero-banner.component').then((m) => m.AppHeroBannerComponent),
    );
  }
}
